import { LoanTermsService } from './../services/loanTermsService';
import { observable, action, flow, makeObservable } from 'mobx';
import { LoanFastrackDocumentStore } from './loanFastrackDocumentStore';
import { ApiResponse, GlobalStore } from '@roc/feature-app-core';

interface Request {
  entityId: string;
  type: string;
  callbackURL: string;
  fullName: string;
  email: string;
}

export class LoanFastrackPaymentsStore {
  private globalStore: GlobalStore;
  private loanTermsService: LoanTermsService;
  public loanFastrackDocumentStore: LoanFastrackDocumentStore;
  private loanId: string = null;
  private cardHolderName: string = null;
  docusignStatus: boolean = false;
  paymentStatus: boolean = false;
  tamariskDocumentUrl: string = null;

  constructor(globalStore) {
    this.globalStore = globalStore;
    this.loanTermsService = new LoanTermsService();
    this.loanFastrackDocumentStore = new LoanFastrackDocumentStore(globalStore);
    makeObservable(this, {
      docusignStatus: observable,
      fetchDocusignAndPaymentStatus: flow,
      fetchTamariskDocument: flow,
      generateFastTrackDocument: flow,
      paymentStatus: observable,
      setDocusignStatus: action,
      setTamariskDocumentUrl: action,
      tamariskDocumentUrl: observable,
      reset: action,
      persistDataAfterDocusignComplete: flow,
    });
  }

  setDocusignStatus(status: boolean) {
    this.docusignStatus = status;
  }

  setLoanId(loanId: string) {
    this.loanId = loanId;
  }

  setCardHolderName(cardHolderName: string) {
    this.cardHolderName = cardHolderName;
  }

  setTamariskDocumentUrl(url: string) {
    this.tamariskDocumentUrl = url;
  }

  *generateFastTrackDocument(signatureDataURL: string) {
    try {
      const data = {
        loanId: this.loanId,
        cardHolderName: this.cardHolderName,
        signature: signatureDataURL,
        userAgent: navigator.userAgent,
      };

      const response: ApiResponse = yield this.loanTermsService.generateFastTrackDocument(
        data
      );

      if (response.data.data) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Fast Track document signed successfully!',
        });
        this.setDocusignStatus(true);
        return true;
      }
    } catch (error) {
      console.error('Error generating Fast Track document:', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error signing Fast Track document. Please try again.',
      });
      return false;
    }
  }

  *fetchDocusignAndPaymentStatus(entityId: string, type: string) {
    try {
      const response: ApiResponse = yield this.loanTermsService.getAppraisalPaymentStatus(
        entityId,
        type
      );
      const { data } = response;

      if (data) {
        this.docusignStatus = data.docusignStatus;
        this.paymentStatus = data.paymentStatus;
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while retrieving payment status',
      });
    }
  }

  *fetchTamariskDocument(request: Request) {
    try {
      const response: ApiResponse = yield this.loanTermsService.getTamariskDocusign(
        request
      );
      const { data } = response;

      this.tamariskDocumentUrl = data ? data : null;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while retrieving acknowledgment document',
      });
    }
  }

  *persistDataAfterDocusignComplete(
    id: string,
    cardholderName: string,
    cardholderEmail: string,
    flowType: string
  ) {
    const response: ApiResponse = yield this.loanTermsService.persistBorrowerDataForFastrack(
      id,
      cardholderName,
      cardholderEmail,
      flowType
    );
  }

  reset() {
    this.docusignStatus = false;
    this.paymentStatus = false;
    this.tamariskDocumentUrl = null;
    this.loanId = null;
    this.cardHolderName = null;
  }
}

export default LoanFastrackPaymentsStore;
